<script>
import Popup from '../../../components/Popup.vue';
import axios from 'axios';
import SearchBar from '../../../components/Inputs/SearchBar.vue';
import SortButton from '../../../components/SortButton.vue';
import ListGrid from '../../../components/AccordionTable/ListGrid.vue';
import CheckboxIndigo from '../../../components/Inputs/CheckboxIndigo.vue';
import ProfilePicture from '../../../components/ProfilePicture.vue';
import ActionButton from '../../../components/ActionButton.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import MessageHeader from '../../../components/MessageHeader.vue';

export default {
  name: 'MassTemplateAssignation',
  components: {
    MessageHeader,
    ActionButton,
    ProfilePicture,
    CheckboxIndigo,
    ListGrid,
    SortButton,
    SearchBar,
    Popup,
  },

  props: {
    opened: {
      type: Boolean,
      required: true,
    },
    template: {
      type: Object,
      required: true,
    },
  },

  emits: ['close'],

  data() {
    return {
      beneficiaries: [],
      selected: [],
      search: '',
      sort: 'ABC',
      forceLoading: false,
    };
  },

  methods: {
    ...mapMutations({
      setMessageHeader: 'template/SET_MESSAGEHEADER',
    }),

    async getBeneficiaries() {
      this.forceLoading = true;
      let beneficiaries = await axios.get('/api/beneficiaries/templates');
      this.beneficiaries = beneficiaries.data;

      for (const beneficiary of this.beneficiaries) {
        if (this.currentTemplate(beneficiary).id === this.template.id) {
          this.selected.push(beneficiary.id);
        }
      }
      this.forceLoading = false;
    },

    currentTemplate(beneficiary) {
      return beneficiary.neopass.templates.filter(
        (template) => template.page_id === this.template.page_id
      )[0];
    },

    lastEval(beneficiary) {
      let lastEval =
        this.currentTemplate(beneficiary)?.last_evaluation?.closure_date;
      if (!lastEval) return 'Pas d\'évaluation sur le modèle';
      return lastEval;
    },

    evaluator(template) {
      return template.last_evaluation?.evaluators[0];
    },

    close() {
      this.$emit('close');
    },

    select(id) {
      if (this.selected.includes(id)) {
        return;
      }

      this.selected.push(id);
    },

    unselect(id) {
      if (!this.selected.includes(id)) {
        return;
      }

      this.selected = this.selected.filter((selectedId) => selectedId !== id);
    },

    async save() {
      const selectedBeneficiaries = this.beneficiaries.filter((benef) =>
        this.selected.includes(benef.id)
      );

      try {
        const {
          data: { success, failed },
        } = await axios.post(
          `/api/template/${this.template.id}/beneficiaries`,
          { beneficiaries: selectedBeneficiaries }
        );

        const { hasTemplate, evalInProgress } = failed.reduce((acc, item) => {
          if (item.reason === 'Already has template') {
            acc.hasTemplate.push(item);
          } else if (item.reason === 'Evaluation in progress') {
            acc.evalInProgress.push(item);
          }
          return acc;
        }, { hasTemplate: [], evalInProgress: []});

        const msg = this.createMessage(success, hasTemplate, evalInProgress);

        this.setMessageHeader({ type: 'success', msg: msg });
        await this.getBeneficiaries();
      } catch (e) {
        console.error(e);
        this.setMessageHeader({ type: 'error', msg: 'Une erreur est survenue, veuillez patienter puis réessayer.'});
      } finally {
        setTimeout(() => {
          this.setMessageHeader(null);
        }, 4500);
      }
    },

    /**
     * Generates a message based on the given parameters.
     *
     * @param {Array} success - An array containing information about the success cases.
     * @param {Array} hasTemplate - An array containing information about the cases that don't need modification.
     * @param {Array} evalInProgress - An array containing information about the cases that couldn't be updated due to an ongoing evaluation.
     *
     * @returns {string} - The generated message.
     */
    createMessage(success, hasTemplate, evalInProgress) {
      const msgParts = [];
      if (success.length > 0) {
        msgParts.push(`Le modèle de page a été assigné à ${success.length} nouveaux bénéficiaires.`);
      }
      if (hasTemplate.length > 0) {
        msgParts.push(`${hasTemplate.length} bénéficiaires n'ont pas eu besoin d'être modifiés.`);
      }
      if (evalInProgress.length > 0) {
        msgParts.push(`${evalInProgress.length} bénéficiaires n'ont pas pu être mis à jour car une évaluation utilisant ce modèle est en cours.`);
      }
      return msgParts.join(' ');
    }
  },

  computed: {
    ...mapGetters({
      messageHeader: 'template/messageHeader',
    }),

    loading() {
      if (this.forceLoading) return true;
      return this.beneficiaries.length === 0;
    },

    allSelected: {
      /**
       * Checks if all the selected beneficiary IDs match with the IDs of all beneficiaries.
       *
       * @returns {boolean} Returns `true` if all the selected beneficiary IDs match with the IDs of all beneficiaries,
       *                    otherwise returns `false`.
       */
      get: function () {
        const allBenefIds = this.beneficiaries.map((benef) => benef.id);
        const allSelectedIds = this.selected.map((id) => id);
        allBenefIds.sort();
        allSelectedIds.sort();

        if (allBenefIds.length !== allSelectedIds.length) {
          return false;
        }

        for (let i = 0; i < allSelectedIds.length; i++) {
          if (allSelectedIds[i] !== allBenefIds[i]) {
            return false;
          }
        }

        return true;
      },

      /**
       * Set function to select or unselect all beneficiaries.
       * If the value is falsy, unselect all beneficiaries.
       * If the value is truthy, select all beneficiaries.
       * @param {boolean} value - The value to indicate whether to select or unselect all beneficiaries.
       */
      set: function (value) {
        if (!value) {
          console.log('unselectAll');
          this.selected = [];
          return;
        }
        console.log('selectAll');
        for (const beneficiary of this.beneficiaries) {
          this.selected.push(beneficiary.id);
        }
      },
    },
  },

  watch: {
    opened: {
      immediate: true,
      handler() {
        if (this.opened) {
          this.getBeneficiaries();
        } else {
          this.beneficiaries = [];
        }
      },
    },
  },
};
</script>

<template>
  <popup
    :opened="opened"
    @close="close()"
  >
    <div class="flex flex-col justify-center items-center">
      <message-header
        class="w-full"
        :message-header="messageHeader"
      />
      <h1 class="mt-10 md:mt-0 text-2xl">
        Attribuer le modèle de page <br>« {{ template.name }} »
      </h1>

      <p class="my-5 text-md text-indigo-600">
        Attribuez ce modèle de page à un ou plusieurs Neopass en cochant les
        bénéficiaires concernés.
      </p>

      <div class="flex flex-col space-y-4 md:space-y-0 md:flex-row w-full justify-between items-center">
        <search-bar
          class="w-full md:w-1/3 lg:w-1/4"
          :value="search"
          placeholder="Rechercher un bénéficiaire"
          @input="(value) => (search = value)"
        />

        <sort-button
          :sort="sort"
          @sorted="(sortType) => (sort = sortType)"
        />
      </div>

      <div
        v-if="loading"
        class="w-full min-h-halfscreen flex justify-center items-center"
      >
        <span class="p-7 border-t-2 border-r-2 rounded-full animate-spin border-indigo-600" />
      </div>
      <list-grid
        v-else
        :list-items="beneficiaries"
        grid="grid-cols-12"
      >
        <template #header>
          <div class="col-start-1 col-span-2 md:col-span-1 ml-2 md:ml-5">
            <checkbox-indigo
              :value="allSelected"
              @input="(newValue) => (allSelected = newValue)"
              libelle="Tous"
            />
          </div>
          <div class="col-span-2 md:col-span-1 hidden md:inline" /> <!-- Profile Picture -->
          <div class="col-start-4 md:col-start-auto col-span-8 md:col-span-3 truncate">
            Bénéficiaires
          </div>
          <div class="md:col-span-3 col-span-4 hidden md:inline">
            Modèle de page actuel
          </div>
          <div class="md:col-span-3 col-span-4 hidden md:inline">
            Dernière évaluation
          </div>
        </template>
        <template #accordionDescription="{ item, num }">
          <div class="col-start-1 col-span-2 md:col-span-1 ml-2 md:ml-5">
            <checkbox-indigo
              :value="selected.includes(item.id)"
              @input="
                (newValue) => (newValue ? select(item.id) : unselect(item.id))
              "
            />
          </div>

          <div class="col-span-2 md:col-span-1">
            <profile-picture
              v-show="loading"
              loading
              size="h-10 w-10"
            />
            <profile-picture
              v-show="!loading"
              :other-object="item"
              size="h-10 w-10"
            />
          </div>

          <div class="col-span-8 md:col-span-3 flex">
            {{ item?.firstname }} {{ item?.lastname }}
          </div>

          <div class="md:col-span-3 col-span-4 hidden md:inline">
            {{ currentTemplate(item).name }}
          </div>

          <div class="md:col-span-3 col-span-4 hidden md:inline">
            {{ lastEval(item) }}<br>
            {{ evaluator(currentTemplate(item)) }}
          </div>
        </template>
      </list-grid>

      <action-button
        class="my-10"
        libelle="Enregistrer"
        responsive
        uppercase
        icon="save"
        border-color="border-transparent"
        text-color="text-white"
        bg-color="bg-pink-600"
        bg-color-square="bg-pink-600"
        icon-color="text-white"
        type-shadow="shadow-pink"
        @clickAction="save"
      />
    </div>
  </popup>
</template>

<style scoped></style>
